import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useUser } from '../providers/user-provider';
import { useToast } from '../providers/toaster-provider';
import routes from '../api/routes';
import buildApiRequestConfig from '../api/url-builder';

const POLL_INTERVAL_MS = 5000;
const DEFER_CHECK_MS = 500;
const LAST_SHOWN_STORAGE_PREFIX = 'provisionAgingLastShown:';

// Re-asks the backend's gate (triggerProvisionAgingIfNeeded's own
// once-per-day check, provision-aging-service.js) periodically while
// this entity stays the active one - without this, a tab left open past the
// gate's window never gets checked again until entityId changes (a fresh
// login, company switch, or impersonate), even though the backend would
// happily allow another run by then. triggerIfNeeded is a fast no-op when
// not yet due, so calling it this often is cheap.
const RECHECK_INTERVAL_MS = 15 * 60 * 1000;

// A run against a small/local dataset can start and finish in single-digit
// milliseconds - faster than this watcher's first status check could ever
// land while it's still 'running'. Without this, a run that just happened
// but was never observed mid-flight would look identical to one that
// finished hours/days ago, and get silently swallowed by the "don't nag on
// ordinary days" rule below. Treating anything started this recently as
// "just happened" (even though we missed the 'running' window) fixes that.
const RECENT_RUN_WINDOW_MS = 2 * 60 * 1000;

// Login sets the user cookie (and this component's entityId) before the
// redirect into the app finishes, so without this the very first status
// check - and any toast it surfaces - can fire while the login screen is
// still on screen. Deferred until we're off these routes instead.
const PUBLIC_ROUTES = ['/login', '/signup', '/forgot-password', '/reset-password', '/admin', '/admin/forgot-password'];

type AgingStatus = 'running' | 'completed' | 'failed' | null;

const fetchStatus = async (entityId: number): Promise<{ status: AgingStatus; startedAt: string | null; summary: any } | null> => {
    const { url, method } = buildApiRequestConfig(routes.provisionAging.fetchStatus, [
        { param: 'entityId', value: entityId }
    ]);
    const token = Cookies.get('token');
    const response = await axios({
        method,
        url,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    return response.data;
};

// Asks the backend to run the once-per-day check/trigger for this entity -
// see triggerProvisionAgingIfNeeded, provision-aging-service.js. Called once
// per entityId here (not from login) specifically so switching companies
// mid-session (manage-company-menu.tsx's confirmCompanySwitch, a purely
// client-side cookie swap with no backend round-trip of its own) still gets
// checked - login alone used to miss that entirely.
const triggerIfNeeded = async (entityId: number): Promise<void> => {
    const { url, method } = buildApiRequestConfig(routes.provisionAging.trigger, [
        { param: 'entityId', value: entityId }
    ]);
    const token = Cookies.get('token');
    await axios({
        method,
        url,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
};

// Both triggers AND watches the provision aging run (see
// triggerProvisionAgingIfNeeded in provision-aging-service.js), surfacing a
// persistent, global toast so the user knows when to refresh the
// Transactions page - the run itself happens invisibly otherwise, in the
// background, at most once per day per entity. Triggering lives here
// (rather than only in loginUser) so it fires whenever entityId becomes the
// active one in the browser, not just at login - switching companies via
// manage-company-menu.tsx is a purely client-side cookie swap with no
// backend round-trip, so login alone used to miss it entirely. Renders
// nothing; mounted once near the app root, inside both UserProvider and
// NotificationProvider.
const ProvisionAgingWatcher: React.FC = () => {
    const { user } = useUser();
    const { showInfo, showSuccess, showError, hideToast } = useToast();
    const entityId = user?.entityId;

    const location = useLocation();
    const locationRef = useRef(location.pathname);
    useEffect(() => {
        locationRef.current = location.pathname;
    }, [location.pathname]);

    useEffect(() => {
        if (!entityId) return;

        let cancelled = false;
        let shownRunningToast = false;
        let triggered = false;
        let pollTimer: ReturnType<typeof setInterval> | null = null;
        let deferTimer: ReturnType<typeof setTimeout> | null = null;
        let recheckTimer: ReturnType<typeof setInterval> | null = null;

        const stopPolling = () => {
            if (pollTimer) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
            if (deferTimer) {
                clearTimeout(deferTimer);
                deferTimer = null;
            }
        };

        const check = async () => {
            if (PUBLIC_ROUTES.includes(locationRef.current)) {
                deferTimer = setTimeout(check, DEFER_CHECK_MS);
                return;
            }
            try {
                // Only on the first check for this entityId per mount, not
                // on every poll tick - triggerIfNeeded itself is already a
                // fast no-op once a run has started/finished today, but
                // there's no reason to hit it repeatedly while polling an
                // already-running job.
                if (!triggered) {
                    triggered = true;
                    await triggerIfNeeded(entityId);
                    if (cancelled) return;
                }

                const data = await fetchStatus(entityId);
                if (cancelled || !data) return;

                if (data.status === 'running') {
                    if (!shownRunningToast) {
                        shownRunningToast = true;
                        showInfo("We're updating your provisions in the background. We'll let you know when it's done.", { persistent: true });
                    }
                    if (!pollTimer) {
                        pollTimer = setInterval(check, POLL_INTERVAL_MS);
                    }
                    return;
                }

                // Terminal (or not configured) on this check. Only worth
                // surfacing anything if we were either polling for it, or it
                // demonstrably just happened (started within the last couple
                // minutes) - otherwise it's just the normal, everyday state
                // (nothing ran today, or it ran hours ago) and telling the
                // user about it now would be noise.
                stopPolling();
                const startedAtMs = data.startedAt ? new Date(data.startedAt).getTime() : 0;
                const justFinished = startedAtMs > 0 && (Date.now() - startedAtMs) < RECENT_RUN_WINDOW_MS;
                if (!shownRunningToast && !justFinished) return;

                // justFinished exists to catch a run that started and ended
                // faster than this watcher's first check could ever land
                // while it's still 'running' - but that window has no memory
                // across page loads: every hard refresh is a fresh mount with
                // shownRunningToast reset to false, so without this, a hard
                // refresh within RECENT_RUN_WINDOW_MS of a real completion
                // just re-triggers the same "finished updating" toast again,
                // every time. localStorage persists across reloads (unlike
                // this component's own state), so key the dedup on the run's
                // own startedAt - a stable identifier for "this specific run"
                // - and only show it once per run, no matter how many times
                // the page reloads afterward. Also marked (not just checked)
                // when shownRunningToast is true - having watched a run live
                // through to completion still needs recording, or a refresh
                // immediately after would hit the justFinished path with
                // nothing on record yet and show one bonus repeat.
                if (data.startedAt) {
                    const storageKey = `${LAST_SHOWN_STORAGE_PREFIX}${entityId}`;
                    try {
                        if (!shownRunningToast && localStorage.getItem(storageKey) === data.startedAt) return;
                        localStorage.setItem(storageKey, data.startedAt);
                    } catch {
                        // localStorage can throw (private browsing, blocked
                        // storage) - fail open rather than crash; worst case
                        // is the toast can repeat in that browser.
                    }
                }

                if (shownRunningToast) {
                    // Dismisses the oldest open toast (our own "running" one,
                    // in the common case nothing else has a persistent toast
                    // open at the same moment) - the toaster provider doesn't
                    // expose closing by id.
                    hideToast();
                }

                if (data.status === 'completed') {
                    showSuccess('Your provisions have finished updating. Refresh this page to see the changes.', { persistent: true });
                } else if (data.status === 'failed') {
                    const summary = (data as any).summary;
                    const reason = summary?.error ? `: ${summary.error}` : '.';
                    showError(`Something went wrong while updating your provisions${reason} Please contact support if this keeps happening.`, { persistent: true });
                }
            } catch (error) {
                stopPolling();
            }
        };

        check();

        // Periodically give the backend gate another chance to say yes, even
        // though entityId hasn't changed - skipped while pollTimer is already
        // actively tracking a running job, since the backend would just
        // refuse another trigger for the same reason anyway.
        recheckTimer = setInterval(() => {
            if (pollTimer) return;
            triggered = false;
            check();
        }, RECHECK_INTERVAL_MS);

        return () => {
            cancelled = true;
            stopPolling();
            if (recheckTimer) {
                clearInterval(recheckTimer);
                recheckTimer = null;
            }
        };
    }, [entityId, showInfo, showSuccess, showError, hideToast]);

    return null;
};

export default ProvisionAgingWatcher;
